import { useCallback, useEffect, useState } from 'react';
import type { ActivityEntry, TaskView } from '@alfred/shared';
import { api, describeError } from './api.js';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'actions', label: 'Actions' },
  { id: 'ai', label: 'AI' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'system', label: 'System' },
] as const;

type Filter = (typeof FILTERS)[number]['id'];

/** Everything Alfred did (the audit log) plus his background tasks. */
export function ActivityView() {
  const [filter, setFilter] = useState<Filter>('all');
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: Filter, before: string | null) => {
    setLoading(true);
    try {
      const page = await api.activity(f, before);
      setEntries((old) => (before ? [...old, ...page.entries] : page.entries));
      setNext(page.nextBefore);
      setError(null);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filter, null);
  }, [filter, load]);

  useEffect(() => {
    void api
      .tasks()
      .then(setTasks)
      .catch(() => {});
  }, []);

  return (
    <div className="view">
      {tasks.length > 0 && (
        <section className="panel">
          <h2 className="panel-title">Background tasks</h2>
          <ul className="task-list">
            {tasks.slice(0, 5).map((t) => (
              <li key={t.id} className="task">
                <span className={`chip chip-${t.status}`}>{TASK_STATUS[t.status]}</span>
                <div className="task-body">
                  <p className="task-request">{t.request}</p>
                  {t.status === 'done' && t.result && (
                    <p className="muted small clamp">{t.result}</p>
                  )}
                  {t.status === 'failed' && t.error && <p className="error small">{t.error}</p>}
                  <p className="muted small">{when(t.createdAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="filters" role="tablist" aria-label="Filter activity">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            className={`chip-btn ${filter === f.id ? 'chip-btn-on' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}
      {!loading && entries.length === 0 && !error && <p className="muted">Nothing here yet.</p>}

      <ol className="activity">
        {entries.map((e, i) => {
          const day = dayLabel(e.at);
          const showDay = i === 0 || dayLabel(entries[i - 1]?.at ?? '') !== day;
          return (
            <li key={e.id}>
              {showDay && <h3 className="day">{day}</h3>}
              <Row entry={e} />
            </li>
          );
        })}
      </ol>

      {next && (
        <button className="btn btn-wide" disabled={loading} onClick={() => void load(filter, next)}>
          {loading ? 'Loading…' : 'Load older'}
        </button>
      )}
    </div>
  );
}

const TASK_STATUS: Record<TaskView['status'], string> = {
  queued: 'Queued',
  running: 'Working',
  done: 'Done',
  failed: 'Failed',
};

function Row({ entry }: { entry: ActivityEntry }) {
  const [open, setOpen] = useState(false);
  const { title, detail } = describe(entry);
  return (
    <div className={`act act-${entry.kind}`}>
      <button className="act-main" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="act-time">{time(entry.at)}</span>
        <span className="act-text">
          <span className="act-title">{title}</span>
          {detail && <span className="act-detail">{detail}</span>}
        </span>
        {entry.costUsd !== null && entry.costUsd > 0 && (
          <span className="act-cost">${entry.costUsd.toFixed(entry.costUsd < 0.01 ? 4 : 3)}</span>
        )}
      </button>
      {open && (
        <div className="act-more">
          <p className="muted small">
            {entry.action}
            {entry.model ? ` · ${entry.model}` : ''}
            {entry.durationMs !== null ? ` · ${(entry.durationMs / 1000).toFixed(1)}s` : ''}
          </p>
          {entry.input !== null && <pre>{JSON.stringify(entry.input, null, 2)}</pre>}
          {entry.result !== null && <pre>{JSON.stringify(entry.result, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
}

/** A plain-English line for each kind of thing Alfred records. */
function describe(e: ActivityEntry): { title: string; detail: string | null } {
  const input = (e.input ?? {}) as Record<string, unknown>;
  const result = (e.result ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v : null);
  const tool = e.toolName ?? 'a tool';
  switch (e.action) {
    case 'agent.turn':
      return {
        title:
          result.ok === false
            ? 'Reply failed'
            : input.channel === 'voice'
              ? 'Replied by phone'
              : 'Replied',
        detail: str(input.prompt),
      };
    case 'model.call':
      return { title: 'Chose a model', detail: e.model };
    case 'tool.read':
      return { title: `Used ${tool}`, detail: str(input.query) ?? str(input.path) };
    case 'tool.draft':
      return { title: `Made a change with ${tool}`, detail: str(input.path) };
    case 'tool.executed':
      return { title: `Carried out approved ${tool} action`, detail: null };
    case 'tool.denied':
      return { title: `Blocked ${tool}`, detail: str(result.reason) };
    case 'tool.failed':
      return { title: `${tool} failed`, detail: str(result.error) };
    case 'approval.requested':
      return { title: 'Asked for your approval', detail: str(input.summary) };
    case 'approval.approved':
      return { title: input.editedFrom ? 'You edited and approved' : 'You approved', detail: null };
    case 'approval.rejected':
      return { title: 'You rejected', detail: str(input.note) };
    case 'killswitch.engaged':
      return { title: 'Kill switch engaged', detail: str(input.reason) };
    case 'killswitch.released':
      return { title: 'Kill switch released', detail: null };
    case 'spend.cap-blocked':
      return { title: 'Stopped at the daily spending limit', detail: null };
    case 'feedback.given':
      return {
        title:
          input.rating === 1
            ? 'You liked a reply'
            : input.rating === -1
              ? 'You disliked a reply'
              : 'You gave feedback',
        detail: str(input.correction),
      };
    case 'auth.login':
      return { title: 'Signed in', detail: str(input.passkey) };
    case 'auth.logout':
      return { title: 'Signed out', detail: null };
    case 'auth.login-failed':
      return { title: 'Failed sign-in attempt', detail: str(result.reason) };
    case 'auth.passkey-registered':
      return { title: 'Passkey added', detail: str(input.name) };
    case 'auth.passkey-removed':
      return { title: 'Passkey removed', detail: null };
    case 'task.queued':
      return { title: 'Started a background task', detail: str(input.request) };
    case 'task.done':
      return { title: 'Finished a background task', detail: str(result.summary) };
    case 'task.failed':
      return { title: 'Background task failed', detail: str(result.error) };
    case 'voice.call':
      return { title: 'Phone call', detail: null };
    case 'memory.saved':
      return { title: 'Remembered', detail: str(input.content) };
    case 'memory.updated':
      return { title: 'Updated a memory', detail: str(input.content) };
    case 'memory.forgotten':
      return { title: 'Forgot', detail: str(input.content) };
    case 'memory.extract':
      return {
        title:
          result.ok === false
            ? 'Couldn’t check a conversation for things to remember'
            : 'Checked a conversation for things to remember',
        detail: null,
      };
    case 'decision.logged':
      return { title: 'Logged a decision', detail: str(input.decision) };
    case 'decision.reviewed':
      return { title: 'Reviewed a decision', detail: str(input.lesson) };
    case 'decision.rescheduled':
      return { title: 'Moved a decision check-in', detail: str(input.decision) };
    case 'decision.follow-up-due':
      return { title: 'Decision check-in due', detail: str(input.decision) };
    case 'x.sync':
      if (typeof result.error === 'string') {
        return { title: 'Couldn’t check your X posts', detail: result.error };
      }
      return {
        title: 'Checked your X posts',
        detail:
          typeof result.imported === 'number' && typeof result.refreshed === 'number'
            ? `${result.imported} new, ${result.refreshed} with fresh stats`
            : null,
      };
    case 'style.updated':
      return {
        title:
          result.ok === false ? 'Couldn’t update your writing style' : 'Updated your writing style',
        detail: null,
      };
    case 'location.device-set-up':
      return { title: 'Phone set up for location', detail: null };
    case 'location.device-removed':
      return { title: 'Phone location turned off', detail: null };
    case 'location.place-saved':
      return {
        title: input.moved ? 'Moved a saved place' : 'Saved a place',
        detail: str(input.name),
      };
    case 'location.place-removed':
      return { title: 'Removed a place', detail: str(input.name) };
    case 'location.reminder-set':
      return { title: 'Set a location reminder', detail: str(input.message) };
    case 'location.reminder-cancelled':
      return { title: 'Cancelled a location reminder', detail: str(input.message) };
    case 'location.reminder-fired':
      return {
        title: `Reminded you at ${str(input.place) ?? 'a place'}`,
        detail: str(input.message),
      };
    case 'location.arrived':
      return { title: `Arrived at ${str(input.place) ?? 'a place'}`, detail: null };
    case 'location.left':
      return { title: `Left ${str(input.place) ?? 'a place'}`, detail: null };
    case 'alerts.notified':
      return { title: str(input.title) ?? 'Sent you an alert', detail: str(input.body) };
    case 'alerts.news-checked':
      return {
        title: 'Checked local news',
        detail: typeof result.headlines === 'number' ? `${result.headlines} new headlines` : null,
      };
    case 'schedule.added':
      return { title: `Scheduled “${str(input.title) ?? ''}”`, detail: str(input.when) };
    case 'schedule.ran':
      return { title: `Ran “${str(input.title) ?? ''}”`, detail: null };
    case 'schedule.reminded':
      return { title: `Reminded you: ${str(input.title) ?? ''}`, detail: null };
    case 'schedule.failed':
      return { title: `Couldn’t run “${str(input.title) ?? ''}”`, detail: str(result.error) };
    case 'schedule.skipped':
      return { title: `Skipped “${str(input.title) ?? ''}” (too late)`, detail: null };
    case 'schedule.paused':
      return { title: `Paused “${str(input.title) ?? ''}”`, detail: null };
    case 'schedule.active':
      return { title: `Resumed “${str(input.title) ?? ''}”`, detail: null };
    case 'schedule.cancelled':
      return { title: `Cancelled “${str(input.title) ?? ''}”`, detail: null };
    case 'monitor.added':
      return { title: `Watching ${str(input.name) ?? 'a site'}`, detail: str(input.url) };
    case 'monitor.removed':
      return { title: `Stopped watching ${str(input.name) ?? 'a site'}`, detail: null };
    case 'monitor.alert':
      return { title: str(input.title) ?? 'Site alert', detail: str(input.body) };
    case 'content.watchlist':
      return { title: 'Updated the content watchlist', detail: str(input.niche) };
    case 'content.run':
      return {
        title: 'Checked TikTok and Reels',
        detail:
          typeof result.found === 'number'
            ? `${result.found} new, ${String(result.relevant)} in your niche, ${String(result.analysed)} broken down`
            : null,
      };
    case 'content.failed':
      return { title: 'Content check failed', detail: str(result.error) };
    case 'content.skipped':
      return { title: 'Skipped a content check', detail: str(result.reason) };
    case 'content.alert':
      return { title: 'Something’s taking off in your niche', detail: str(input.url) };
    case 'apikey.saved':
      return { title: `Saved an API key: ${str(input.service) ?? ''}`, detail: null };
    case 'apikey.removed':
      return { title: `Removed an API key: ${str(input.service) ?? ''}`, detail: null };
    case 'budget.set':
      return {
        title: `Budget: ${str(input.name) ?? str(input.feature) ?? ''} → ${typeof input.monthlyUsd === 'number' ? `$${input.monthlyUsd}/month` : 'no limit'}`,
        detail: str(input.basis),
      };
    case 'budget.blocked':
      return {
        title: `Paused ${str(input.feature) ?? 'a feature'}: monthly budget used up`,
        detail: null,
      };
    case 'budget.fixed-set':
      return { title: `Fixed cost: ${str(input.label) ?? ''}`, detail: null };
    case 'budget.fixed-removed':
      return { title: 'Removed a fixed cost', detail: null };
    case 'evals.run':
      return {
        title: 'Ran your test suite',
        detail:
          typeof result.avgScore === 'number'
            ? `average ${result.avgScore}/10, ${Math.round(Number(result.passRate) * 100)}% passed`
            : null,
      };
    case 'evals.case-added':
      return { title: 'Added a test', detail: str(input.prompt) };
    case 'evals.harvested':
      return { title: `Added ${String(input.added)} tests from your feedback`, detail: null };
    case 'evals.review':
      return { title: 'Wrote your weekly review', detail: null };
    case 'upgrade.approved':
      return { title: `Upgrade queued: ${str(input.title) ?? ''}`, detail: null };
    case 'upgrade.done':
      return { title: `Upgrade built: ${str(input.title) ?? ''}`, detail: str(input.note) };
    case 'upgrade.dropped':
      return { title: `Upgrade dropped: ${str(input.title) ?? ''}`, detail: str(input.note) };
    case 'skill.added':
      return { title: `Saved skill “${str(input.name) ?? ''}”`, detail: null };
    case 'skill.updated':
      return { title: `Updated skill “${str(input.name) ?? ''}”`, detail: str(input.why) };
    case 'skill.removed':
      return { title: `Removed skill “${str(input.name) ?? ''}”`, detail: null };
    case 'models.trial':
      return {
        title: `Tried ${str(input.to) ?? 'a model'} as the ${str(input.role) ?? ''} model`,
        detail: str(result.verdict),
      };
    case 'models.switched':
      return {
        title: input.reverted
          ? `Put the ${str(input.role) ?? ''} model back to ${str(input.to) ?? 'the default'}`
          : `Switched the ${str(input.role) ?? ''} model to ${str(input.to) ?? '?'}`,
        detail: str(input.from) ? `was ${str(input.from)}` : null,
      };
    case 'video.persona':
      return { title: `Set up presenter ${str(input.name) ?? ''}`, detail: null };
    case 'video.footage-added':
      return { title: 'Added footage', detail: str(input.description) };
    case 'video.batch':
      return {
        title: `Ordered ${String(input.videos)} videos (${String(input.lines)} presenter lines)`,
        detail: typeof input.estimateUsd === 'number' ? `about $${input.estimateUsd}` : null,
      };
    case 'video.clip':
      return { title: `Presenter line ready (${str(input.slot) ?? ''})`, detail: null };
    case 'video.ready':
      return { title: `Video ready: ${str(input.title) ?? ''}`, detail: null };
    case 'video.failed':
      return { title: `Video failed: ${str(input.title) ?? ''}`, detail: str(result.error) };
    case 'video.posted':
      return { title: 'You posted a video', detail: null };
    case 'video.remix':
      return { title: 'Planned a remix', detail: null };
    case 'video.daily-remix':
      return { title: input.on ? 'Daily remixes on' : 'Daily remixes off', detail: null };
    case 'reddit.watchlist':
      return { title: 'Updated the Reddit watchlist', detail: null };
    case 'xtrends.watchlist':
      return { title: 'Updated the X watchlist', detail: null };
    case 'xtrends.run':
      return {
        title: 'Scanned X for winning posts',
        detail:
          typeof result.found === 'number'
            ? `${result.found} posts, ${String(result.winners)} winners, ${String(result.drafted)} drafts`
            : null,
      };
    case 'xtrends.read':
      return {
        title: `Checked ${String(input.posts ?? '')} winning posts’ numbers on X`,
        detail: null,
      };
    case 'xtrends.analysed':
      return { title: `Broke down a winning X post by @${str(input.author) ?? '?'}`, detail: null };
    case 'xtrends.draft':
      return { title: 'Drafted an X post from a winning format', detail: str(input.note) };
    case 'xtrends.reply-draft':
      return { title: 'Drafted an X reply for you to post', detail: str(input.to) };
    case 'xtrends.judged':
      return { title: `Sorted ${String(input.posts ?? '')} X posts for relevance`, detail: null };
    case 'reddit.run':
      return {
        title: 'Scanned Reddit',
        detail:
          typeof result.found === 'number'
            ? `${result.found} posts, ${String(result.opportunities)} openings, ${String(result.drafted)} drafts`
            : null,
      };
    case 'reddit.draft':
      return {
        title: `Drafted for r/${str(input.subreddit) ?? '?'}`,
        detail: str(input.thread) ?? str(input.post),
      };
    case 'reddit.posted':
      return { title: `You posted in r/${str(input.subreddit) ?? '?'}`, detail: null };
    case 'reddit.playbook':
      return { title: 'Updated Reddit playbooks', detail: null };
    case 'reddit.judged':
      return { title: 'Sorted Reddit threads', detail: null };
    case 'content.blueprint-removed':
      return { title: `Removed video plan “${str(input.title) ?? ''}”`, detail: null };
    case 'content.blueprint-failed':
      return { title: 'Couldn’t make a video plan', detail: str(result.error) };
    case 'content.blueprint':
      return { title: `Wrote a blueprint: ${str(input.title) ?? ''}`, detail: str(input.basedOn) };
    case 'content.report':
      return { title: 'Wrote your content report', detail: null };
    case 'content.analysis-failed':
      return { title: 'Couldn’t break down a video', detail: str(result.error) };
    case 'google.connected':
      return { title: 'Connected Google', detail: str(input.email) };
    case 'google.disconnected':
      return { title: 'Disconnected Google', detail: null };
    case 'briefing.sent':
      return { title: 'Sent your morning briefing', detail: str(input.headline) };
    case 'briefing.settings':
      return {
        title: input.enabled
          ? `Briefing set for ${str(input.time) ?? ''}`
          : 'Turned the briefing off',
        detail: null,
      };
    case 'x.connected':
      return { title: 'Connected X', detail: str(input.username) };
    default:
      return { title: e.action, detail: null };
  }
}

const dayFmt = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

function dayLabel(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return dayFmt.format(d);
}

function time(iso: string) {
  return timeFmt.format(new Date(iso));
}

function when(iso: string) {
  return `${dayLabel(iso)}, ${time(iso)}`;
}
